import { z } from 'zod';

/**
 * The single place that decides what a meeting panel open/close does to the URL, and the single
 * place that declares which search params the panels own.
 *
 * Pure on purpose: no router, no React. Everything here is a function of
 * (current search, surface, intent, which surface the current history entry was pushed for),
 * which is what makes the whole branch matrix testable without a browser.
 *
 * Panel order is deliberately absent. Browser history already records it: opening
 * Chat pushes `?panel=chat`, opening Settings on top pushes `?panel=chat&settings=1`,
 * and back restores the entry where only Chat was open.
 */

/** Chat, notes, participants and the checklist share one side panel, so a single param makes two of them at once unrepresentable. */
export const exclusivePanelSurfaces = ['chat', 'notes', 'participants', 'checklist'] as const;

/** Each of these is an independent flag param of the same name, and several can be open at once. */
export const flagPanelSurfaces = ['settings', 'invite', 'more'] as const;

export const meetingPanelSurfaces = [...exclusivePanelSurfaces, ...flagPanelSurfaces] as const;

export type ExclusivePanelSurface = (typeof exclusivePanelSurfaces)[number];
export type FlagPanelSurface = (typeof flagPanelSurfaces)[number];
export type MeetingPanelSurface = (typeof meetingPanelSurfaces)[number];

/** The only value a flag param ever holds while its surface is open. */
export const PANEL_FLAG_ON = '1';

/**
 * The router parses a search value with `JSON.parse`, so `?settings=1` arrives as the number 1
 * and `?settings=true` as a boolean. A flag is read as text either way.
 */
const flagSchema = z.coerce.string().optional();

/**
 * The panel-owned slice of the `/join/$meetingId` search params, and the only declaration of it:
 * the route spreads these fields into its own schema and the types below are inferred from them,
 * so a new panel is one edit here. A stranger's `?panel=whatever` degrades to nothing open rather
 * than failing the whole route.
 */
export const meetingPanelSearchFields = {
	panel: z.enum(exclusivePanelSurfaces).optional().catch(undefined),
	settings: flagSchema,
	invite: flagSchema,
	more: flagSchema,
};

export const meetingPanelSearchSchema = z.object(meetingPanelSearchFields);

export type MeetingPanelSearch = z.infer<typeof meetingPanelSearchSchema>;

/** Every panel-owned search key. `satisfies` is what makes a key that drifts off the schema a type error. */
export const meetingPanelSearchKeys = [
	'panel',
	...flagPanelSurfaces,
] as const satisfies readonly (keyof MeetingPanelSearch)[];

/** Every panel param cleared at once, for the strip on entry. */
export const clearedMeetingPanelSearch: MeetingPanelSearch = Object.fromEntries(
	meetingPanelSearchKeys.map((key) => [key, undefined]),
);

/** The params a decision changes, merged over the current search by the caller. `undefined` clears one. */
export type MeetingPanelSearchPatch = Partial<Record<FlagPanelSurface, typeof PANEL_FLAG_ON | undefined>> & {
	panel?: ExclusivePanelSurface | undefined;
};

export type PanelIntent = 'open' | 'close' | 'toggle';

/** Write the patch into a new history entry (`push`) or over the current one (`replace`). */
export interface PanelSearchDecision {
	action: 'push' | 'replace';
	search: MeetingPanelSearchPatch;
}

/**
 * `none` when the surface already holds the value asked for, and `back` to pop the entry this
 * very panel pushed - neither writes anything into the address.
 */
export type PanelDecision = { action: 'none' } | { action: 'back' } | PanelSearchDecision;

export interface SurfaceOpenQuery {
	/** The current `/join/$meetingId` search params. */
	search: MeetingPanelSearch;
	surface: MeetingPanelSurface;
}

export interface PanelDecisionInput extends SurfaceOpenQuery {
	intent: PanelIntent;
	/**
	 * The surface the current history entry was pushed for, when this module pushed it. Closing
	 * pops only the entry belonging to the surface being closed: popping an entry someone else
	 * pushed would dismiss the wrong panel, or eject the user from the meeting entirely.
	 */
	stampedSurface?: MeetingPanelSurface;
	/**
	 * The surface being handed off from, when one directly opens another - the mobile
	 * "More options" drawer opening Notes. The handoff replaces, so no dead entry is left.
	 */
	supersedes?: MeetingPanelSurface;
}

const flagSurfaceNames: ReadonlySet<string> = new Set<string>(flagPanelSurfaces);
const allSurfaceNames: ReadonlySet<string> = new Set<string>(meetingPanelSurfaces);

function isFlagSurface(surface: MeetingPanelSurface): surface is FlagPanelSurface {
	return flagSurfaceNames.has(surface);
}

/** Narrows an untrusted value - history state, a search param - to a surface, or to nothing. */
export function asMeetingPanelSurface(value: unknown): MeetingPanelSurface | undefined {
	if (typeof value !== 'string' || !allSurfaceNames.has(value)) return undefined;
	return meetingPanelSurfaces.find((surface) => surface === value);
}

export function isSurfaceOpen({ search, surface }: SurfaceOpenQuery): boolean {
	if (isFlagSurface(surface)) return search[surface] === PANEL_FLAG_ON;
	return search.panel === surface;
}

function openPatch(surface: MeetingPanelSurface): MeetingPanelSearchPatch {
	if (isFlagSurface(surface)) return { [surface]: PANEL_FLAG_ON };
	return { panel: surface };
}

function closePatch(surface: MeetingPanelSurface): MeetingPanelSearchPatch {
	if (isFlagSurface(surface)) return { [surface]: undefined };
	return { panel: undefined };
}

export function decide({ search, surface, intent, stampedSurface, supersedes }: PanelDecisionInput): PanelDecision {
	const alreadyOpen = isSurfaceOpen({ search, surface });
	// A toggle is not write-only: it resolves against the search that is actually in the address.
	const closing = intent === 'close' || (intent === 'toggle' && alreadyOpen);

	if (closing) {
		if (!alreadyOpen) return { action: 'none' };

		// Rule 6: closing never navigates forward. Pop the entry only when this very surface is the
		// one that pushed it. Anything else - a deep link, an auto-open, a redirect, or another panel
		// stacked on top since - is rewritten in place instead. Popping there would dismiss whatever
		// sits on top rather than this surface, and on a deep link it would leave the meeting.
		if (stampedSurface === surface) return { action: 'back' };
		return { action: 'replace', search: closePatch(surface) };
	}

	const handsOffFrom =
		supersedes !== undefined && supersedes !== surface && isSurfaceOpen({ search, surface: supersedes })
			? supersedes
			: undefined;

	// Rule 3, the important one: a push whose target param already holds the target value is a
	// no-op. It covers a double tap and a toggle fired while open - anything separated by a
	// re-render, so that the second call decides against a search that already shows the panel.
	// It does not cover two calls inside one commit: both see the same pre-open search and both
	// return `push`. Only one entry comes out of that, but TanStack is what makes it one, by
	// dropping a navigation whose URL and state already match the current location.
	if (alreadyOpen) {
		// Unless something is handing off to it. Tapping Notes in the More drawer while Notes is
		// already open behind it still has to shut the drawer, or it is stranded over the panel
		// with nothing but the back gesture left to dismiss it.
		if (handsOffFrom === undefined) return { action: 'none' };
		return { action: 'replace', search: closePatch(handsOffFrom) };
	}

	// Rule 5: a direct handoff replaces, so the drawer it came from leaves no entry behind.
	if (handsOffFrom !== undefined) {
		return { action: 'replace', search: { ...closePatch(handsOffFrom), ...openPatch(surface) } };
	}

	// Rule 4: the exclusive surfaces are a tab bar, and a tab switch does not push. The invariant is
	// one history entry per panel session however much the user flicks between the tabs inside it.
	if (!isFlagSurface(surface) && search.panel !== undefined) return { action: 'replace', search: openPatch(surface) };

	// Rule 2: opening a surface that was not open is a push.
	return { action: 'push', search: openPatch(surface) };
}
